import { useCallback } from 'react'
import { observer } from 'mobx-react-lite'
import {
  Box,
  Button,
  Divider,
  Error,
  IconCopyButton,
  Tooltip,
  Typography,
  Warning,
} from 'shared/ui'
import { makeTextFieldElement, makeTextFieldHighlightLimit, TextField } from 'shared/ui/TextField'
import { LINK_SUPPORT } from 'shared/constants/links'
import { DoubleOptInSettingLayout } from 'pages/settings/pages/doubleOptIn/ui/doubleOptInSetting/DoubleOptInSettingLayout'
import { useInboxLayerContext } from 'pages/settings/pages/inboxes/context/inboxLayerContext'
import styles from './styles.module.scss'

export const MessagingGeneral = observer(() => {
  const {
    inboxSettingsStore: { messagingGeneralStore, updateCurrentTeamName, inbox },
  } = useInboxLayerContext()
  const {
    name,
    warningMessage,
    errorMessage,
    limit,
    setLimitReached,
    setName,
    saveName,
    colorCodingEnabled,
    useOrgHistory,
    inboxHistory,
    isInboxHistoryDisabled,
    toggleColorCoding,
    toggleUseOrgHistory,
    toggleInboxHistory,
    openDeleteInboxModal,
    hasOnlyOneInbox,
    restrictManageDeleteInbox,
  } = messagingGeneralStore

  const handleBlur = useCallback(async () => {
    const saved = await saveName()
    if (saved) {
      updateCurrentTeamName(name)
    }
  }, [saveName, name])

  return (
    <div className={styles.wrap}>
      <Typography ariaLabel={'MessagingGeneralTitle'} variant={'body-lg-medium'}>
        General
      </Typography>
      <Box display={'flex'} flexDirection={'column'}>
        <div className={styles.inboxSection}>
          <div className={styles.description}>
            <Typography
              variant='body-md-medium'
              color='var(--content-primary-primary)'
              ariaLabel='InboxName_title'
              marginBottom={4}
            >
              Inbox name
            </Typography>
            <Typography
              variant='body-md-regular'
              color='var(--content-primary-tertiary)'
              ariaLabel='InboxName_desc'
            >
              Enter a short, descriptive name for this inbox
            </Typography>
          </div>
          <div className={styles.inputWrapper}>
            <TextField
              className={styles.nameField}
              variant='stroke'
              warning={warningMessage}
              error={errorMessage}
              value={name}
              onChange={setName}
              onBlur={handleBlur}
              ariaLabel={'InboxName_Input'}
              mainActions={[
                makeTextFieldHighlightLimit({
                  limit: limit,
                  onChange: setLimitReached,
                }),
              ]}
              rightActions={[
                makeTextFieldElement({
                  element: (
                    <IconCopyButton
                      value={inbox?.inbox_identifier || ''}
                      showTooltip
                      tooltipProps={{
                        label: `Inbox ID: ${inbox?.inbox_identifier}`,
                        type: 'description',
                        placement: 'top',
                        desc: 'Click to copy this unique Inbox ID used for syncing with integration partners',
                        maxWidth: 240,
                      }}
                    />
                  ),
                }),
              ]}
            />
            <div className={styles.inboxSectionError}>
              <Error type={'text'} error={errorMessage} />
              <Warning text={warningMessage} />
            </div>
          </div>
        </div>
        <DoubleOptInSettingLayout
          title={'Color coding'}
          description={'Enable color coding to highlight how recent each conversation is'}
          checked={colorCodingEnabled}
          onToggle={toggleColorCoding}
          tooltipProps={{
            label: 'Colors show how recent a conversation is:',
            type: 'description',
            placement: 'top',
            desc: (
              <>
                Blue – last 8 hours <br />
                Yellow – last 24 hours <br />
                Red – more than 24 hours old
              </>
            ),
          }}
        />
        <div className={styles.section}>
          <Typography ariaLabel={'ConversationsHistory'} variant={'body-lg-medium'}>
            Conversations history
          </Typography>
          <div>
            <DoubleOptInSettingLayout
              title={'Use organization settings'}
              description={'Use the organization-wide setting for conversation history'}
              checked={useOrgHistory}
              onToggle={toggleUseOrgHistory}
            />
            <Divider style={{ marginTop: '8px', marginBottom: '7px' }} />
            <DoubleOptInSettingLayout
              title={'Inbox conversations history'}
              description={
                'Show conversations from this inbox in the conversations history timeline'
              }
              checked={inboxHistory}
              onToggle={toggleInboxHistory}
              disabled={isInboxHistoryDisabled}
            />
          </div>
        </div>
        {!restrictManageDeleteInbox && (
          <>
            <div className={styles.section}>
              <Typography ariaLabel={'InboxManagement'} variant={'body-lg-medium'}>
                Inbox management
              </Typography>
              <div className={styles.removeInboxSection}>
                <div className={styles.description}>
                  <Typography
                    variant='body-md-medium'
                    color='var(--content-primary-primary)'
                    ariaLabel='RemoveInbox_title'
                    marginBottom={4}
                  >
                    Remove inbox
                  </Typography>
                  <Typography
                    variant='body-md-regular'
                    color='var(--content-primary-tertiary)'
                    ariaLabel='RemoveInbox_desc'
                  >
                    Delete this inbox and all its conversations
                  </Typography>
                </div>
                <div className={styles.footerActions}>
                  <Tooltip
                    label={
                      <>
                        To remove this inbox please contact <br />
                        <a href={`${LINK_SUPPORT}`} target='_blank' rel='noreferrer'>
                          Customer Support
                        </a>
                        .
                      </>
                    }
                    placement={'top'}
                    breakWord
                    visibility={hasOnlyOneInbox ? 'visible' : 'hidden'}
                  >
                    <Button
                      text={'Delete inbox'}
                      typeBtn={'contained'}
                      contained={'secondary'}
                      intent={'destructive'}
                      icon={'delete'}
                      disabled={hasOnlyOneInbox}
                      onClick={openDeleteInboxModal}
                    />
                  </Tooltip>
                </div>
              </div>
            </div>
          </>
        )}
      </Box>
    </div>
  )
})
